/**
 * Règles métier PURES — partagées entre SaaS Nuxt et widget standalone.
 *
 * Fonctions sans effet de bord, sans I/O, sans Vue. Importées des deux côtés
 * pour garantir la cohérence des validations / calculs métier.
 */

import type {
  Animal,
  Availability,
  JourneyOption,
  Meal,
  Passenger,
  RequestItem,
  SearchState,
  Tariff,
} from '../types'
import { Direction, PassengerType } from '../types/enums'

// Réexport du moteur calendrier pur (grille, dédup départs, dates dispo).
export * from './calendar'

// Réexport des règles d'âge pures : bandes provider (`resolveAgeBands`), âge à
// une date de départ (`ageAt`), bornes de saisie et validation de la date de
// naissance. Source unique widget ↔ SaaS.
export * from './ages'

// Réexport des fonctions PURES d'assemblage du payload de création de ticket
// (mapping passager / tarif pricing / leg availability / place). Partagées
// widget ↔ SaaS, sans couplage de store.
export * from './ticket-payload'

// Recherche de modèle de véhicule (nom + alias, sans accents ni séparateurs).
export * from './vehicle-search'

export function isReadyForSearch(state: SearchState): boolean {
  const hasOutwardJourney = Array.isArray(state.journeys) && state.journeys.length > 0
  const hasPassengers = Array.isArray(state.passengers) && state.passengers.length > 0
  if (!hasOutwardJourney || !hasPassengers) return false
  const allRequestItems = collectAllRequestItems(state)
  const hasOutwardDate = allRequestItems.some((item) => item.out_at && item.out_at !== '')
  if (!hasOutwardDate) return false
  if (state.direction === Direction.Return) {
    const hasReturnDate = allRequestItems.some((item) => item.ret_at && item.ret_at !== '')
    if (!hasReturnDate) return false
  }
  return true
}

export function collectAllRequestItems(state: SearchState): RequestItem[] {
  const req = state.request || {}
  return Object.values(req).flatMap((items) => (Array.isArray(items) ? items : []))
}

export interface PassengerCounts {
  adults: number
  children: number
  babies: number
  total: number
}

export function countPassengers(passengers: Passenger[]): PassengerCounts {
  const counts = { adults: 0, children: 0, babies: 0, total: 0 }
  for (const p of passengers) {
    counts.total++
    if (p.type === PassengerType.Adult) counts.adults++
    else if (p.type === PassengerType.Child) counts.children++
    else if (p.type === PassengerType.Baby) counts.babies++
  }
  return counts
}

/**
 * Nombre total d'animaux (chats + chiens + chiens >30kg). Source unique pour
 * les compteurs/résumés « pets » côté SaaS (`petsCounter`) et widget.
 * Tolère un `animal` partiel/absent (fallback 0 par champ).
 */
export function countPets(animal: Animal | null | undefined): number {
  if (!animal) return 0
  return (animal.cats || 0) + (animal.dogs || 0) + (animal.dogs30 || 0)
}

/** `true` dès qu'au moins un animal est sélectionné. */
export function hasPets(animal: Animal | null | undefined): boolean {
  return countPets(animal) > 0
}

// `buildAgeRange` (plage d'âges du PREMIER journey) a été retirée : les deux
// sélecteurs dérivent désormais leurs âges de `resolvePaxProfile().band`
// (cf. `./ages`), qui consolide les sous-tranches ET intersecte tous les
// journeys de la route.

/**
 * Compteurs cibles passés à {@link rebuildPassengers}, indexés par la `key` des
 * {@link RebuildKindConfig} — une entrée par TRANCHE tarifaire, pas par type :
 * un transporteur peut vendre « Jeunesse », « Adulte » et « Senior »
 * séparément.
 */
export type RebuildTarget = Record<string, number>

/** Configuration d'une tranche de passagers pour la reconstruction. */
export interface RebuildKindConfig {
  key: string
  type: PassengerType
  param: string
  /**
   * Âge attribué à un passager NOUVELLEMENT créé dans cette tranche. `index` =
   * position (0-based) du passager créé dans son groupe.
   */
  defaultAge: (index: number) => number
}

/**
 * Reconstruit une liste de passagers depuis des compteurs cibles, en
 * RÉUTILISANT les passagers de la même tranche (donc en préservant leurs âges
 * déjà saisis) puis en complétant via `defaultAge`. Les `order` sont réindexés
 * séquentiellement (1-based).
 *
 * L'appariement se fait sur `type` + `param` : deux tranches d'un même type
 * (Jeunesse / Senior) ne doivent pas se voler leurs passagers.
 *
 * Pure : aucune dépendance store/Vue.
 */
export function rebuildPassengers(
  existing: Passenger[],
  target: RebuildTarget,
  kinds: readonly RebuildKindConfig[],
): Passenger[] {
  const claimed = new Set<Passenger>()
  const result: Passenger[] = []
  let order = 1

  for (const kind of kinds) {
    const wanted = target[kind.key] ?? 0
    const kept: Passenger[] = []

    for (const pax of existing ?? []) {
      if (kept.length >= wanted) break
      if (claimed.has(pax) || pax.type !== kind.type || pax.param !== kind.param) continue
      claimed.add(pax)
      kept.push(pax)
    }

    for (const pax of kept) result.push({ ...pax, order: order++ })
    for (let i = kept.length; i < wanted; i++) {
      result.push({
        order: order++,
        type: kind.type,
        age: kind.defaultAge(i),
        param: kind.param,
        gender: 'Male',
      })
    }
  }

  return result
}

// ════════════════════════════════════════════════════════════════════════
//  PRICING — règles pures (source unique SaaS ↔ widget)
//
//  Ces fonctions reproduisent VERBATIM la logique de référence du SaaS
//  (ex-`PriceCalculator.calculateTotalPrice` + `computePayToday`, jadis dans
//  `src/nuxt/utils/pricing.ts`). Aucune dépendance Nuxt/Vue/store : elles
//  prennent une liste de tarifs plats et un total meals déjà agrégé.
//
//  Compat double-shape du champ `pricing` :
//    - SaaS  → `pricing.client_price` (backend réel, markup chaîne inclus)
//    - widget→ `pricing.display_amount`
//  `tariffDisplayAmount` lit `client_price` PUIS `display_amount` PUIS `amount`,
//  ce qui préserve à l'identique le comportement des deux stacks.
// ════════════════════════════════════════════════════════════════════════

/** Arrondi au centime supérieur — identique au `ceilToCents` du SaaS. */
export function ceilToCents(n: number): number {
  return Math.ceil(n * 100) / 100
}

/** Tarif minimal accepté par les fonctions de pricing (structurel). */
type PricingTariffLike = {
  type?: string | null
  amount?: number | null
  balance_amount?: number | null
  currency?: string | null
  pricing?: {
    client_price?: number | null
    display_amount?: number | null
    display_currency?: string | null
  } | null
}

/**
 * Montant d'affichage d'un tarif (POV agency). Préfère le prix preview
 * (`client_price` côté SaaS, `display_amount` côté widget), fallback sur le
 * carrier brut `amount`. Retourne `0` si rien d'exploitable.
 *
 * NB : ne loggue PAS (fonction pure). Le SaaS conserve son `console.warn` de
 * dégradation dans `utils/shared.ts.getDisplayAmount`, qui délègue ici.
 */
export function tariffDisplayAmount(t: PricingTariffLike | null | undefined): number {
  if (!t) return 0
  const client = Number(t.pricing?.client_price)
  if (Number.isFinite(client)) return client
  const display = Number(t.pricing?.display_amount)
  if (Number.isFinite(display)) return display
  const raw = Number(t.amount)
  return Number.isFinite(raw) ? raw : 0
}

/**
 * Devise d'affichage d'un tarif (POV agency), fonction sœur de
 * {@link tariffDisplayAmount}. Préfère `pricing.display_currency` (devise du
 * prix preview chaîné), fallback sur le carrier brut `currency`, puis `'EUR'`.
 * Garde le formatage (Intl/toLocale) côté composant.
 */
export function tariffDisplayCurrency(t: PricingTariffLike | null | undefined): string {
  if (!t) return 'EUR'
  return t.pricing?.display_currency ?? t.currency ?? 'EUR'
}

function tariffKind(t: PricingTariffLike): string {
  return String(t?.type ?? '').toLowerCase()
}

/**
 * Total commande (référence SaaS `PriceCalculator.calculateTotalPrice`) :
 *   - ticket  → + montant d'affichage
 *   - presale → + montant d'affichage + balance_amount (brut, sans markup)
 *   - option  → + balance_amount (brut)
 *   - + mealsTotal (déjà agrégé par l'appelant)
 *
 * `mealsTotal` est passé par l'appelant car le calcul des repas dépend de
 * l'état d'availability (store SaaS) qui n'a pas sa place dans une règle pure.
 */
export function computeTariffsTotal(
  tariffs: PricingTariffLike[],
  opts?: { mealsTotal?: number },
): number {
  let total = 0
  for (const t of tariffs ?? []) {
    const kind = tariffKind(t)
    const amount = tariffDisplayAmount(t)
    const balance = Number(t?.balance_amount ?? 0)
    switch (kind) {
      case 'ticket':
        total += amount
        break
      case 'presale':
        total += amount + balance
        break
      case 'option':
        total += balance
        break
      default:
        // Types inconnus ignorés (comportement SaaS : log only, pas d'ajout).
        break
    }
  }
  return total + Number(opts?.mealsTotal ?? 0)
}

/**
 * Montant à payer aujourd'hui (référence SaaS `computePayToday`) :
 *   1. Si ≥1 presale → ceil(Σ presale.amount)  [montant CARRIER brut, pas le
 *      display ni le balance — verbatim SaaS]
 *   2. Sinon si ≥1 option → 0
 *   3. Sinon si ≥1 ticket → total complet
 *   4. Fallback (aucun type reconnu) → total complet
 */
export function computePayTodayAmount(
  tariffs: PricingTariffLike[],
  opts?: { mealsTotal?: number },
): number {
  const list = tariffs ?? []
  const presales = list.filter((t) => tariffKind(t) === 'presale')
  const options = list.filter((t) => tariffKind(t) === 'option')
  const tickets = list.filter((t) => tariffKind(t) === 'ticket')

  if (presales.length > 0) {
    const sum = presales.reduce((acc, t) => acc + Number(t?.amount ?? 0), 0)
    return ceilToCents(sum)
  }
  if (options.length > 0) return 0
  if (tickets.length > 0) return computeTariffsTotal(list, opts)
  return computeTariffsTotal(list, opts)
}

export interface BookingTotal {
  amount: number
  currency: string
  breakdown: { tariffs: number; options: number; fees: number; insurance: number }
}

/**
 * @deprecated Conservé pour les call sites widget d'affichage de total. Le
 * total des tarifs délègue désormais à {@link computeTariffsTotal} (logique
 * presale/option/acompte unifiée SaaS). Options/fees/insurance restent
 * additionnés tels quels.
 */
export function computeBookingTotal(input: {
  tariffs: Tariff[]
  options?: Array<{ price: number; quantity: number; currency: string }>
  fees?: Array<{ amount: number; currency: string }>
  insurance?: { active: boolean; amount: number }
}): BookingTotal {
  const tariffsSum = computeTariffsTotal(input.tariffs)
  let optionsSum = 0
  let feesSum = 0
  let insuranceSum = 0
  let currency = 'EUR'
  for (const t of input.tariffs) {
    if (t.currency) currency = t.currency
  }
  for (const opt of input.options ?? []) optionsSum += (opt.price ?? 0) * (opt.quantity ?? 1)
  for (const fee of input.fees ?? []) feesSum += fee.amount ?? 0
  if (input.insurance?.active) insuranceSum = input.insurance.amount ?? 0
  return {
    amount: tariffsSum + optionsSum + feesSum + insuranceSum,
    currency,
    breakdown: { tariffs: tariffsSum, options: optionsSum, fees: feesSum, insurance: insuranceSum },
  }
}

export function pickCheapestTariff(availability: Availability): Tariff | null {
  return pickCheapestByType(availability, 'ticket')
}

/** Sélectionne le tarif le moins cher d'un type donné parmi tous les tariffs
 *  d'une availability. Utilisé par le widget pour exposer ticket / presale /
 *  option indépendamment quand le sailing les propose. */
export function pickCheapestByType(availability: Availability, type: string): Tariff | null {
  const wanted = String(type).toLowerCase()
  const matching = Object.values(availability.tariffs || {})
    .filter((t) => String(t.type ?? '').toLowerCase() === wanted)
  if (matching.length === 0) return null
  return matching.reduce<Tariff | null>((cheapest, current) => {
    if (!cheapest) return current
    const cheapAmount = cheapest.pricing?.display_amount ?? cheapest.amount
    const curAmount = current.pricing?.display_amount ?? current.amount
    return curAmount < cheapAmount ? current : cheapest
  }, null)
}

/**
 * Clé d'identité d'un sailing (traversée) : un horaire de départ pour une
 * compagnie donnée. Plusieurs availabilities partagent ce sailing — chez
 * certains providers (Balearia) il y a UNE availability par type de place,
 * toutes sous le même sailing. Source unique pour regrouper (Results) ET pour
 * retrouver les places du sailing sélectionné (PlaceList) — sans elle, les deux
 * divergeaient et seules les places de la 1ʳᵉ availability s'affichaient.
 */
export function sailingKey(av: Availability): string {
  const company = typeof av.company === 'object' ? (av.company?.name ?? '') : String(av.company ?? '')
  return `${av.depart_at ?? ''}__${company}`
}

/**
 * Retrouve l'availability qui porte une place donnée. Indispensable quand
 * « une place = une availability » (Balearia) : la place choisie détermine
 * l'availability réellement réservée (son `id` + son `price_token` signé).
 * `null` si la place n'appartient à aucune availability de la liste.
 */
export function availabilityForPlace(
  list: Availability[],
  placeId: string | null | undefined,
): Availability | null {
  if (!placeId) return null
  return list.find((av) => (av.places ?? []).some((p) => String(p.id) === String(placeId))) ?? null
}

// ════════════════════════════════════════════════════════════════════════
//  GROUPES PASSAGERS — découpage d'un dossier > 4 pax en sous-groupes.
//
//  Règle métier (backend) : au-delà de MAX_GROUP_SIZE passagers, la recherche
//  est scindée en 2 sous-process (group 1 = les MAX_GROUP_SIZE premiers,
//  group 2 = le reste). Le backend renvoie les availabilities taguées
//  `group_id: 1|2` dans la MÊME réponse. Chaque groupe = une sélection de
//  place + un ticket distinct (même booking_id/reference). SOURCE UNIQUE
//  partagée SaaS ↔ widget.
// ════════════════════════════════════════════════════════════════════════

/** Taille maximale d'un sous-groupe de passagers (limite provider). */
export const MAX_GROUP_SIZE = 4

/** Numéro de groupe métier : 1 (toujours présent) ou 2 (reliquat > MAX_GROUP_SIZE). */
export type GroupNumber = 1 | 2

/** Clé de slot store (`group1`/`group2`) pour un numéro de groupe. */
export function groupKey(group: GroupNumber): 'group1' | 'group2' {
  return group === 1 ? 'group1' : 'group2'
}

/** Un 2ᵉ groupe est requis dès que le dossier dépasse MAX_GROUP_SIZE passagers. */
export function requiresSecondGroup(passengerCount: number): boolean {
  return passengerCount > MAX_GROUP_SIZE
}

/** Liste des groupes à traiter pour un effectif donné : `[1]` ou `[1, 2]`. */
export function activeGroups(passengerCount: number): GroupNumber[] {
  return requiresSecondGroup(passengerCount) ? [1, 2] : [1]
}

/**
 * Availabilities d'un groupe donné (group_id absent ⇒ groupe 1 par défaut).
 *
 * Règle métier : un groupe 2 n'existe JAMAIS seul. Si la liste ne contient
 * aucune availability de groupe 1, le groupe 2 est traité comme vide — on ne
 * propose / ne réserve pas un sous-groupe orphelin (cohérence du dossier).
 */
export function availabilitiesForGroup(list: Availability[], group: GroupNumber): Availability[] {
  const source = list ?? []
  const g1 = source.filter((av) => Number(av.group_id ?? 1) === 1)
  if (group === 1) return g1
  if (g1.length === 0) return []
  return source.filter((av) => Number(av.group_id ?? 1) === 2)
}

/**
 * Sous-ensemble de passagers porté par un groupe : group 1 = les
 * MAX_GROUP_SIZE premiers (ordre du tableau), group 2 = le reste. Pour un
 * effectif ≤ MAX_GROUP_SIZE : group 1 = tous, group 2 = [].
 */
export function passengersForGroup<T>(passengers: T[], group: GroupNumber): T[] {
  const list = passengers ?? []
  return group === 1 ? list.slice(0, MAX_GROUP_SIZE) : list.slice(MAX_GROUP_SIZE)
}

/**
 * Passagers du dossier alloués à une place, dans l'ORDRE du dispatch backend
 * `place.in_board.passengers` (liste d'`order`). Mappe chaque `order` à l'objet
 * passager correspondant (références modifiables du store → édition de la saisie).
 * `null` si la place ne porte pas d'allocation → l'appelant retombe sur le
 * découpage par taille (`passengersForGroup`). C'est `in_board` qui guide.
 */
export function passengersForPlace<T extends { order: number }>(
  place: { in_board?: { passengers?: Array<{ order?: number | null }> | null } | null } | null | undefined,
  allPassengers: T[],
): T[] | null {
  const board = place?.in_board?.passengers
  if (!Array.isArray(board) || board.length === 0) return null
  const byOrder = new Map((allPassengers ?? []).map((p) => [Number(p.order), p]))
  const out: T[] = []
  for (const bp of board) {
    const p = byOrder.get(Number(bp?.order))
    if (p) out.push(p)
  }
  return out.length > 0 ? out : null
}

/** Availability la moins chère d'une liste (par tarif cheapest), ou `null`. */
export function cheapestAvailability(list: Availability[]): Availability | null {
  let best: Availability | null = null
  let bestAmount = Number.POSITIVE_INFINITY
  for (const av of list ?? []) {
    const t = pickCheapestTariff(av)
    if (!t) continue
    const amount = t.amount ?? 0
    if (amount < bestAmount) {
      bestAmount = amount
      best = av
    }
  }
  return best
}

export function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `fg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

// ════════════════════════════════════════════════════════════════════════
//  MEALS — règle d'unification + total (source unique SaaS ↔ widget)
//
//  Règle métier verbatim : les `meals[]` legacy (group1 + group2, sans filtre
//  de type) sont PRIORITAIRES. S'il n'y en a aucun, on retombe sur les
//  `options[]` de type 'RI' (group1 uniquement). Pas de double-affichage : les
//  deux formats portent les mêmes données chez certains providers.
// ════════════════════════════════════════════════════════════════════════

/**
 * Bloc de groupe d'une availability (group1 / group2) tel que lu côté UI.
 * Structurel et tolérant : SaaS et widget passent leur propre objet store.
 */
export interface MealGroupBlock {
  meals?: Meal[] | null
  options?: JourneyOption[] | null
}

/** Shape d'availability par direction restreinte aux blocs group1/group2. */
export interface MealAvailabilityDir {
  group1?: MealGroupBlock | null
  group2?: MealGroupBlock | null
}

/**
 * Repas unifié — superset des deux shapes UI. Chaque côté mappe ce modèle vers
 * sa propre forme de rendu (clés `_name`/`_price` côté SaaS, `name`/`amount`
 * côté widget) ; la SÉLECTION legacy-vs-RI et le calcul de prix sont ici.
 *
 *   - `source` : 'legacy' (meals[]) ou 'option' (options RI).
 *   - `raw`    : l'objet provider d'origine (pour `setMealChanges` côté SaaS).
 *   - `quantity` : quantité courante (0 par défaut pour la source 'option' —
 *     l'appelant peut la réhydrater via son store, cf. SaaS).
 */
export interface UnifiedMeal {
  key: string
  code: string
  name: string
  description: string
  amount: number
  currency: string
  quantity: number
  source: 'legacy' | 'option'
  /** Repas ou service (wifi, VIP…). */
  kind: 'meal' | 'service'
  raw: Meal | JourneyOption
}

/**
 * Construit la liste unifiée des repas pour une direction, en appliquant la
 * priorité legacy > options RI. Pure : prend l'availability de la direction en
 * entrée explicite (pas de store). `fallbackCurrency` est la devise d'affichage
 * résolue par l'appelant si l'item n'en porte pas.
 */
export function buildUnifiedMeals(
  availabilityDir: MealAvailabilityDir | null | undefined,
  opts?: { fallbackCurrency?: string },
): UnifiedMeal[] {
  const fallbackCurrency = opts?.fallbackCurrency ?? 'EUR'
  const result: UnifiedMeal[] = []

  // Legacy meals — group1 + group2, sans filtre de type. On tague le numéro de
  // groupe sur le `raw` (1/2) car le mutateur SaaS `setMealChanges` route la
  // mise à jour via `meal.group` (no-op pour le widget qui ignore ce champ).
  for (const [g, groupNum] of [['group1', 1], ['group2', 2]] as const) {
    const block = availabilityDir?.[g]
    for (const m of block?.meals ?? []) {
      // Repas compris dans le tarif : déjà payé, rien à choisir.
      if (m.included) continue
      result.push({
        key: `legacy-${m.id || m.code || m.name}`,
        code: m.code || m.id,
        name: m.name || m.code || '',
        description: m.description || '',
        amount: Number(m.price || 0),
        currency: m.currency || fallbackCurrency,
        quantity: Number(m.quantity || 0),
        source: 'legacy',
        kind: m.kind ?? (String(m.type ?? '').toUpperCase() === 'SE' ? 'service' : 'meal'),
        raw: { ...m, group: groupNum },
      })
    }
  }

  if (result.length > 0) return result

  // Sinon : options type RI (group1 uniquement).
  const options = availabilityDir?.group1?.options ?? []
  for (const opt of options) {
    if (String(opt.type ?? '').toUpperCase() !== 'RI') continue
    result.push({
      key: `option-${opt.code}`,
      code: opt.code,
      name: opt.name || opt.code || '',
      description: opt.description || '',
      amount: Number(opt.amount || 0),
      currency: opt.currency || fallbackCurrency,
      quantity: Number(opt.quantity || 0),
      source: 'option',
      kind: 'meal',
      raw: opt,
    })
  }
  return result
}

/**
 * Total des repas : Σ (legacy `price` × quantité) + Σ (RI `amount` × quantité).
 * Verbatim SaaS `calculateMealPrice` + `calculateMealOptionsPrice`. Prend les
 * listes brutes en entrée explicite (aucun store).
 */
export function computeMealsTotal(
  meals: Array<{ price?: number | null; quantity?: number | null; included?: boolean }> | null | undefined,
  options: Array<{ type?: string | null; amount?: number | null; quantity?: number | null }> | null | undefined,
): number {
  let total = 0
  for (const m of meals ?? []) {
    if (m?.included) continue // compris dans le tarif
    total += Number(m?.price || 0) * Number(m?.quantity || 0)
  }
  for (const opt of options ?? []) {
    if (String(opt?.type ?? '').toUpperCase() !== 'RI') continue
    total += Number(opt?.amount || 0) * Number(opt?.quantity || 0)
  }
  return total
}

/**
 * Persiste la quantité choisie pour un repas EN MUTANT sa source (in-place).
 * Source unique de la mutation, partagée widget ↔ SaaS — pendant écriture de
 * {@link buildUnifiedMeals} (lecture). Retrouve l'objet RÉEL dans `dir` car le
 * `raw` d'un {@link UnifiedMeal} de source `meals[]` est une COPIE (taggée
 * `group`) et non l'objet du store :
 *   - source `meals[]`  → `dir.group{1,2}.meals` par `id` (fallback `code`),
 *     selon le `group` porté par le `raw` ;
 *   - source `options`  → `dir.group1.options` (type RI) par `code`.
 *
 * Couvre indifféremment les deux formats que le backend renvoie selon le
 * provider — aucun chemin spécifique par stack. No-op si la cible est absente.
 * Quantité normalisée en entier ≥ 0.
 */
export function setUnifiedMealQuantity(
  dir: MealAvailabilityDir | null | undefined,
  meal: UnifiedMeal,
  quantity: number,
): void {
  const qty = Math.max(0, Math.trunc(Number(quantity) || 0))
  if (meal.source === 'legacy') {
    const raw = meal.raw as Meal & { group?: number }
    const block = raw.group === 2 ? dir?.group2 : dir?.group1
    const target = (block?.meals ?? []).find((m) => (m.id || m.code) === (raw.id || raw.code))
    if (target) target.quantity = qty
    return
  }
  const target = (dir?.group1?.options ?? []).find((o) => o.code === meal.code)
  if (target) target.quantity = qty
}

// ════════════════════════════════════════════════════════════════════════
//  HELPERS D'AFFICHAGE (purs) — la fonction retourne une CLÉ/valeur, le `t()`
//  et le formatage restent côté UI.
// ════════════════════════════════════════════════════════════════════════

/**
 * Type de tarif → token canonique i18n (`'ticket' | 'presale' | 'option'`),
 * sinon `null` (type inconnu → l'UI affiche le label brut). L'UI compose la clé
 * dans SON namespace : widget `tariff.${token}`, SaaS `Ticket/Presale/Option`.
 *
 * NB : ne fait PAS le mapping d'alias SaaS `base/standard/full → ticket` (resté
 * local au `friendly()` SaaS), pour ne pas altérer son comportement observable.
 */
export function tariffTypeI18nKey(type: string | null | undefined): 'ticket' | 'presale' | 'option' | null {
  const k = String(type ?? '').toLowerCase()
  if (k === 'ticket') return 'ticket'
  if (k === 'presale') return 'presale'
  if (k === 'option') return 'option'
  return null
}

/**
 * Durée de traversée en HEURES décimales depuis `depart`/`arrive` ISO, ou `null`
 * si invalide / aberrante (≤ 0 ou > 7 jours — bornes verbatim widget). Le
 * formatage (`formatDuration`, en minutes) reste côté UI.
 */
export function computeCrossingDuration(
  depart: string | null | undefined,
  arrive: string | null | undefined,
): number | null {
  if (!depart || !arrive) return null
  const d = Date.parse(depart)
  const a = Date.parse(arrive)
  if (!Number.isFinite(d) || !Number.isFinite(a)) return null
  const diffMs = a - d
  if (diffMs <= 0 || diffMs > 7 * 24 * 3600 * 1000) return null
  return diffMs / 3600000
}

/**
 * Clé compagnie d'un objet portant `.company` (sailing, ticket, journey). Gère
 * `company` string | `{ id }` | `{ name }`. Source unique partagée widget/SaaS
 * pour comparer/regrouper par compagnie.
 */
export function companyKey(holder: unknown): string {
  const c = (holder as { company?: unknown })?.company
  if (typeof c === 'string') return c
  const obj = c as { id?: unknown; name?: unknown } | null | undefined
  return String(obj?.id ?? obj?.name ?? '')
}

/**
 * Un leg (availability / sailing / ticket) porte-t-il un tarif A/R réduit
 * `roundtrip_locked` (ex. Balearia) ? Lit `.tariffs` DÉFENSIVEMENT : tarif
 * unique porté à la racine (vue ticket SaaS), array OU Record (availability).
 * Source unique partagée widget/SaaS pour alimenter {@link canCombineLegs}.
 */
export function isRoundtripLocked(holder: unknown): boolean {
  const tariffs = (holder as { tariffs?: unknown })?.tariffs
  if (!tariffs || typeof tariffs !== 'object') return false
  // Tarif unique porté directement (Ticket.tariffs) → flag à la racine.
  if ((tariffs as { roundtrip_locked?: boolean }).roundtrip_locked === true) return true
  // Array OU Record de tarifs → verrouillé si l'un l'est.
  const list = Array.isArray(tariffs) ? tariffs : Object.values(tariffs as Record<string, unknown>)
  return list.some((t) => (t as { roundtrip_locked?: boolean })?.roundtrip_locked === true)
}

/**
 * Règle d'appariement aller-retour : un leg `roundtrip_locked` (tarif A/R réduit,
 * ex. Balearia) ne se combine qu'avec la MÊME compagnie. Les providers non
 * verrouillés sont mixables entre eux. Source unique partagée widget/SaaS.
 */
export function canCombineLegs(
  outCompany: string,
  outLocked: boolean,
  retCompany: string,
  retLocked: boolean,
): boolean {
  return outCompany === retCompany || (!outLocked && !retLocked)
}
